import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Seo } from "@/components/Layout";
import { Overline } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Flame, BookMarked, Target, TrendingUp, AlertCircle, Heart, Play, Settings, Download, Trash2, Brain } from "lucide-react";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid={`stat-${label}`}>
      <Icon size={18} className="text-primary mb-3" />
      <p className="font-display font-black text-2xl">{value}</p>
      <p className="text-xs text-muted-foreground">{label}{sub ? ` · ${sub}` : ""}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, updateProfile, logout } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then(({ data }) => setD(data)); }, []);

  const exportData = async () => { const { data } = await api.get("/auth/export"); const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "mis-datos-hallosuiza.json"; a.click(); };
  const deleteAcc = async () => { if (!window.confirm("¿Seguro que quieres eliminar tu cuenta? Esta acción no se puede deshacer.")) return; await api.delete("/auth/account"); toast.success("Cuenta eliminada."); await logout(); window.location.href = "/"; };

  if (!d) return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">Cargando panel...</div>;

  return (
    <>
      <Seo title="Mi panel" noindex />
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          <Overline>Panel del alumno</Overline>
          <h1 className="font-display font-black text-3xl md:text-4xl">Hola, {user?.name} 👋</h1>
          <p className="text-muted-foreground mt-1">Nivel actual: <strong className="text-foreground">{d.level}</strong></p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={Flame} label="Racha" value={`${d.streak} días`} />
            <Stat icon={BookMarked} label="Palabras dominadas" value={d.words_mastered} />
            <Stat icon={TrendingUp} label="Cursos activos" value={d.active_courses.length} />
            <Stat icon={Target} label="Objetivo semanal" value={`${d.weekly_goal_min}′`} />
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex justify-between items-center mb-2"><h2 className="font-display font-semibold">Progreso semanal</h2><span className="text-sm text-muted-foreground">{d.weekly_done_min} / {d.weekly_goal_min} min</span></div>
            <Progress value={Math.min(100, (d.weekly_done_min / Math.max(1, d.weekly_goal_min)) * 100)} />
          </div>

          {d.recommended_lesson && (
            <div className="rounded-lg border-2 border-primary bg-card p-6">
              <Overline>Lección recomendada</Overline>
              <h2 className="font-display font-bold text-xl">{d.recommended_lesson.title}</h2>
              <Link to={`/lecciones/${d.recommended_lesson.slug}`}><Button className="mt-4 rounded-full" data-testid="dashboard-recommended"><Play size={16} className="mr-1" />Continuar</Button></Link>
            </div>
          )}

          {d.active_courses.map((c) => (
            <div key={c.slug} className="rounded-lg border border-border bg-card p-6">
              <div className="flex justify-between items-center mb-2"><h3 className="font-display font-semibold">{c.title}</h3><span className="text-xs px-2 py-0.5 rounded-full bg-secondary">{c.level}</span></div>
              <Progress value={(c.completed / c.total) * 100} className="mb-2" />
              <p className="text-sm text-muted-foreground">{c.completed} de {c.total} lecciones completadas</p>
            </div>
          ))}

          <div>
            <h2 className="font-display font-semibold mb-3">Historial de actividad</h2>
            {d.recent_activities.length ? (
              <div className="rounded-lg border border-border bg-card divide-y divide-border">
                {d.recent_activities.map((a, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-3 text-sm">
                    <span>{a.type} · {a.lesson_slug}</span>
                    <span className={a.correct ? "text-emerald-600" : "text-amber-600"}>{a.correct ? "Correcto" : "A repasar"}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Aún no hay actividad. ¡Empieza una lección!</p>}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-3"><AlertCircle size={16} className="text-amber-600" /><h3 className="font-display font-semibold">Errores para repasar</h3></div>
            {d.errors_to_review.length ? (
              <ul className="space-y-2 text-sm text-muted-foreground">{d.errors_to_review.map((e, i) => <li key={i} className="border-l-2 border-amber-500 pl-3">{e.detail || e.lesson_slug}</li>)}</ul>
            ) : <p className="text-sm text-muted-foreground">¡Sin errores pendientes!</p>}
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-3"><Heart size={16} className="text-primary" /><h3 className="font-display font-semibold">Favoritos</h3></div>
            {d.favorites.length ? (
              <ul className="space-y-2 text-sm">{d.favorites.map((fav, i) => <li key={i}><Link to={`/${fav.kind === "guide" ? "guias" : fav.kind === "lesson" ? "lecciones" : "herramientas"}/${fav.slug}`} className="text-primary hover:underline">{fav.title}</Link></li>)}</ul>
            ) : <p className="text-sm text-muted-foreground">Guarda lecciones y guías como favoritas.</p>}
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-3"><Settings size={16} /><h3 className="font-display font-semibold">Mi cuenta</h3></div>
            <div className="space-y-2">
              <Link to="/repaso"><Button size="sm" className="w-full justify-start" data-testid="dashboard-review"><Brain size={14} className="mr-2" />Repaso inteligente</Button></Link>
              <Link to="/conversaciones"><Button variant="outline" size="sm" className="w-full justify-start">Conversaciones con IA</Button></Link>
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={exportData} data-testid="export-data"><Download size={14} className="mr-2" />Exportar mis datos</Button>
              <Button variant="ghost" size="sm" className="w-full justify-start text-destructive" onClick={deleteAcc} data-testid="delete-account"><Trash2 size={14} className="mr-2" />Eliminar cuenta</Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
